import React from 'react';

import type { GuideMediaAsset } from '../types';

const AUTHORIZATION_TOKEN_STORAGE_KEY = 'authorization-token';
const CREATIVE_TESTING_ASSET_PATH_PREFIX = '/api/creative-testing/v1/asset/';

const createObjectUrl = (blob: Blob) => URL.createObjectURL(blob);

export const isAuthenticatedGuideAssetUrl = (source?: string) => {
  if (!source) return false;
  if (typeof window === 'undefined') return false;

  try {
    const parsed = new URL(source, window.location.origin);
    return parsed.pathname.startsWith(CREATIVE_TESTING_ASSET_PATH_PREFIX);
  } catch {
    return false;
  }
};

export const fetchAuthenticatedGuideMedia = async (source: string) => {
  const token = window.localStorage.getItem(AUTHORIZATION_TOKEN_STORAGE_KEY) || '';
  const headers = new Headers();

  if (token) {
    headers.set(AUTHORIZATION_TOKEN_STORAGE_KEY, token);
  }

  const response = await fetch(source, {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Unable to load media (${response.status})`);
  }

  return createObjectUrl(await response.blob());
};

type AuthenticatedGuideMediaProps = {
  asset: GuideMediaAsset;
};

export default function AuthenticatedGuideMedia({
  asset,
}: AuthenticatedGuideMediaProps) {
  const source = asset.src || '';
  const requiresAuthenticatedFetch = isAuthenticatedGuideAssetUrl(source);
  const [resolvedSource, setResolvedSource] = React.useState(
    requiresAuthenticatedFetch ? '' : source,
  );

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    if (!requiresAuthenticatedFetch) {
      setResolvedSource(source);
      return undefined;
    }

    setResolvedSource('');

    fetchAuthenticatedGuideMedia(source)
      .then(nextSource => {
        if (cancelled) {
          URL.revokeObjectURL(nextSource);
          return;
        }

        objectUrl = nextSource;
        setResolvedSource(nextSource);
      })
      .catch(() => {
        if (!cancelled) setResolvedSource('');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [requiresAuthenticatedFetch, source]);

  if (asset.type === 'video') {
    return <video controls src={resolvedSource || undefined} />;
  }

  return <img alt={asset.alt || asset.name} src={resolvedSource || undefined} />;
}
